import React, { useState, useEffect,useMemo } from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, FormControl, InputLabel, Select, MenuItem, TextField, Stack, Button } from '@mui/material';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import { MentionCard } from '../components/MentionCard';
import { api,generateTopicCloud } from '../api';
import WordCloud from "react-d3-cloud";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Import necessary icons
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PublicIcon from '@mui/icons-material/Public';

// A reusable component for the top-row KPI cards with weekly insights
const KpiCard = ({ title, value, insight, isPositive }) => (
    <Card sx={{ height: '120px' }}>
        <CardContent>
            <Typography variant="h5" color="text.primary" sx={{ mb: 1 }}>{title}</Typography>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                {value}
            </Typography>
            {insight && (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color: isPositive ? 'success.main' : 'error.main', mt: 0.5 }}>
                    {isPositive ? <ArrowUpwardIcon sx={{ fontSize: '1rem' }} /> : <ArrowDownwardIcon sx={{ fontSize: '1rem' }} />}
                    <Typography variant="caption">{insight}</Typography>
                </Stack>
            )}
        </CardContent>
    </Card>
);

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});




const DashboardPage = ({ state, onGenerateReply,refreshData }) => {
    const [filters, setFilters] = useState({
        keywordId: 'all',
        platform: 'all',
        startDate: '',
        endDate: ''
    });
    const [filteredMentions, setFilteredMentions] = useState(state.mentions || []);

useEffect(() => {
    // This function will now only run a single time.
    const timer = setTimeout(() => {
      console.log("Refreshing dashboard data after 2-second delay...");
      if (refreshData) {
        refreshData();
      }
    }, 2000); // (Using 2000ms as requested in the previous turn)

    return () => clearTimeout(timer);
    
  }, []); // <-- The dependency array is now empty.
  
    const [locations, setLocations] = useState([]);

    useEffect(() => {
        let data = state.mentions || [];
        if (filters.keywordId !== 'all' && filters.keywordId) data = data.filter(m => m.keywordId == filters.keywordId);
        if (filters.platform !== 'all') data = data.filter(m => m.platform === filters.platform);
        if (filters.startDate) data = data.filter(m => new Date(m.publishedAt) >= new Date(filters.startDate));
        if (filters.endDate) data = data.filter(m => new Date(m.publishedAt) <= new Date(filters.endDate));
        setFilteredMentions(data);
    }, [filters, state.mentions]);

       useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await api.get('/locations');
                setLocations(response.data);
            } catch (error) {
                console.error("Failed to fetch map locations:", error);
            }
        };
        fetchLocations();
    }, [state.mentions]); // Refetch locations if the base mentions data changes

    useEffect(() => {
    setFilteredMentions(state.mentions);
  }, [state.mentions]);

    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleApplyFilters = () => {
      let data = state.mentions;
      if (filters.keywordId !== 'all') data = data.filter(m => m.keywordId == filters.keywordId);
      if (filters.platform !== 'all') data = data.filter(m => m.platform === filters.platform);
      if (filters.startDate) data = data.filter(m => new Date(m.publishedAt) >= new Date(filters.startDate));
      if (filters.endDate) data = data.filter(m => new Date(m.publishedAt) <= new Date(filters.endDate));
      setFilteredMentions(data);
  };

    // --- Data Preparation for Charts & KPIs ---

    // Data for the current period
    const sentimentCounts = filteredMentions.reduce((acc, m) => {
        acc[m.sentiment || 'Unanalyzed'] = (acc[m.sentiment || 'Unanalyzed'] || 0) + 1;
        return acc;
    }, {});
   

    
    // --- THIS IS THE FIX ---
    // The logic inside this useMemo hook has been updated.
    const { pos, neg, neu, overallScore, prevPeriodMentions, prevPos, prevNeg, prevNeu, scoreChange } = useMemo(() => {
        
        // 1. Calculate current period stats (this is correct, as it uses 'filteredMentions')
        const sentimentCounts = filteredMentions.reduce((acc, m) => {
            acc[m.sentiment || 'Unanalyzed'] = (acc[m.sentiment || 'Unanalyzed'] || 0) + 1;
            return acc;
        }, {});
        const p = sentimentCounts.Positive || 0;
        const n = sentimentCounts.Negative || 0;
        const ne = sentimentCounts.Neutral || 0;
        const totalAnalyzed = p + n;
        const score = totalAnalyzed > 0 ? ((p / totalAnalyzed) * 100) : 0;
        
        // 2. Calculate previous period stats (This logic is now fixed)
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        
        // Start with all mentions from the previous period
        let prevMentions = (state.mentions || []).filter(m => new Date(m.publishedAt) < twentyFourHoursAgo);

        // !! APPLY THE SAME FILTERS (except date) to the previous period data !!
        if (filters.keywordId !== 'all' && filters.keywordId) {
            prevMentions = prevMentions.filter(m => m.keywordId == filters.keywordId);
        }
        if (filters.platform !== 'all') {
            prevMentions = prevMentions.filter(m => m.platform === filters.platform);
        }
        
        // Now `prevMentions` is the correct "apples-to-apples" comparison data.
        const prevCounts = prevMentions.reduce((acc, m) => {
            acc[m.sentiment || 'Unanalyzed'] = (acc[m.sentiment || 'Unanalyzed'] || 0) + 1;
            return acc;
        }, {});
        const p_prev = prevCounts.Positive || 0;
        const n_prev = prevCounts.Negative || 0;
        const ne_prev = prevCounts.Neutral || 0;
        const prevTotalAnalyzed = p_prev + n_prev;
        const prevScore = prevTotalAnalyzed > 0 ? ((p_prev / prevTotalAnalyzed) * 100) : 0;
        const sChange = score - prevScore;

        return { 
            pos: p, neg: n, neu: ne, overallScore: score, 
            prevPeriodMentions: prevMentions, // This is now the *filtered* previous mentions
            prevPos: p_prev, prevNeg: n_prev, prevNeu: ne_prev, scoreChange: sChange 
        };
    // The dependency array now includes 'filters' to ensure this recalculates
    }, [filteredMentions, state.mentions, filters]); 

    const calculateChange = (current, previous) => {
        if (previous === 0) return current > 0 ? '+100%' : '0%';
        const change = ((current - previous) / previous) * 100;
        return `${change >= 0 ? '+' : ''}${change.toFixed(0)}% Over the last 24hours`;
    };

     const doughnutData = {
    labels: ['Positive', 'Negative', 'Neutral'],
    datasets: [{
      data: [pos, neg, neu],
      backgroundColor: ['#22c55e', '#ef4444', '#9ca3af'], // bright green, red, grey
      borderWidth: 0,
      cutout: '70%' // thinner ring like in design
    }]
  };

  // Mentions over time
const oneMonthAgo = new Date();
oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

const mentionsByDay = filteredMentions
    .filter(m => new Date(m.publishedAt) >= oneMonthAgo) // This new line filters the data
    .reduce((acc, m) => {
        const date = new Date(m.publishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        acc[date] = (acc[date] || 0) + 1;
        return acc;
    }, {});

   
    const sortedDates = Object.keys(mentionsByDay).sort((a, b) => new Date(a) - new Date(b));


    const platformCounts = filteredMentions.reduce((acc, m) => {
        acc[m.platform] = (acc[m.platform] || 0) + 1;
        return acc;
    }, {});
    if (platformCounts.twitter) {
  platformCounts.X = platformCounts.twitter;
  delete platformCounts.twitter;
}
   const mentionsByPlatformData = {
    labels: Object.keys(platformCounts),
    datasets: [{
      label: 'Mentions',
      data: Object.values(platformCounts),
      backgroundColor: '#6366f1', // indigo like screenshot
      borderRadius: 8, // rounded bars
      barThickness: 30
    }]
  };

   const mentionsOverTimeData = useMemo(() => {
    // Get the date 30 days ago
    const oneMonthAgo = new Date();
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

    // Step 1: Group mentions by day AND sentiment
    const mentionsByDay = (filteredMentions || [])
      // --- THIS IS THE FIX ---
      // First, filter the mentions to only include those from the last 30 days
      .filter(m => new Date(m.publishedAt) >= oneMonthAgo)
      // Now, reduce the filtered array
      .reduce((acc, m) => {
        const date = new Date(m.publishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        if (!acc[date]) {
            acc[date] = { total: 0, Positive: 0, Negative: 0 };
        }
        acc[date].total += 1;
        if (m.sentiment === 'Positive') {
            acc[date].Positive += 1;
        } else if (m.sentiment === 'Negative') {
            acc[date].Negative += 1;
        }
        return acc;
      }, {});
    
    const sortedDates = Object.keys(mentionsByDay).sort((a, b) => new Date(a) - new Date(b));

    // Step 2: Create separate data arrays for each line
    const totalData = sortedDates.map(date => mentionsByDay[date].total);
    const positiveData = sortedDates.map(date => mentionsByDay[date].Positive);
    const negativeData = sortedDates.map(date => mentionsByDay[date].Negative);

    return {
        labels: sortedDates,
        datasets: [
            {
                label: 'Total Mentions',
                data: totalData,
                borderColor: '#3b82f6', // Blue
                tension: 0.1,
                fill: false,
                pointRadius: 0
            },
            {
                label: 'Positive',
                type: 'bar',
                data: positiveData,
                borderColor: '#10B981', // Green
                backgroundColor: '#10B981',
                tension: 0.1,
                fill: false,
                pointRadius: 0
            },
            {
                label: 'Negative',
                type: 'bar',
                data: negativeData,
                borderColor: '#EF4444', // Red
                backgroundColor: '#EF4444',
                tension: 0.1,
                fill: false,
                pointRadius: 0
            }
        ]
    };
  }, [filteredMentions]);

    const topicCloudData = generateTopicCloud(filteredMentions);

    const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } };

  return (
    <Container maxWidth="100%">

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3} sx={{ minHeight: 50 , minWidth:'20%'}}>
              <FormControl fullWidth>
                <InputLabel>Keyword</InputLabel>
                <Select name="keywordId" value={filters.keywordId} onChange={handleFilterChange} label="Keyword">
                  <MenuItem value="all">All Keywords</MenuItem>
                  {Array.isArray(state.keywords) && state.keywords.map(kw => <MenuItem key={kw.id} value={kw.id}>{kw.term}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3} sx={{ minHeight: 50 , minWidth:'20%'}}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select name="platform" value={filters.platform} onChange={handleFilterChange} label="Platform">
                  {['all', 'twitter', 'youtube', 'facebook','facebook-group', 'instagram', 'linkedin', 'News', 'tiktok'].map(p => (
                    <MenuItem key={p} value={p}>{p === 'twitter' ? 'X' : p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={3} sx={{ minHeight: 50 , minWidth:'20%'}}>
              <TextField name="startDate" value={filters.startDate} onChange={handleFilterChange} type="date" label="Start Date" InputLabelProps={{ shrink: true }} fullWidth />
            </Grid>

            <Grid item xs={6} md={3} sx={{ minHeight: 50 , minWidth:'20%'}}>
              <TextField name="endDate" value={filters.endDate} onChange={handleFilterChange} type="date" label="End Date" InputLabelProps={{ shrink: true }} fullWidth />
            </Grid>

              <Grid item xs={12} md={2} sx={{ minHeight: 40 , minWidth:'10%'}}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleApplyFilters}
                    sx={{
                        height: '56px', // Match height of TextFields
                        backgroundColor: '#212529', // Black background
                        color: '#f8f9fa', // Grey text
                        '&:hover': {
                            backgroundColor: '#495057' // A slightly lighter black for hover
                        }
                    }}
                >
                    Apply Filters
                </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* KPI Row (icons + Total Reach) */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2} sx={{ minHeight: 125 , minWidth:'49.2%'}}><KpiCard title="Total Mentions" value={filteredMentions.length.toLocaleString()} insight={calculateChange(filteredMentions.length, prevPeriodMentions.length)} isPositive={filteredMentions.length >= prevPeriodMentions.length} /></Grid>
        <Grid item xs={12} sm={6} md={2}sx={{ minHeight: 125 , minWidth:'49.2%'}}><KpiCard title="Sentiment Score" value={`${overallScore.toFixed(1)}%`} insight={`${scoreChange >= 0 ? '+' : ''}${scoreChange.toFixed(1)}%`} isPositive={scoreChange >= 0} /></Grid>
        <Grid item xs={12} sm={4} md={3}sx={{ minHeight: 125 , minWidth:'32%'}}><KpiCard title="Positive Mentions" value={pos.toLocaleString()} insight={calculateChange(pos, prevPos)} isPositive={pos >= prevPos} /></Grid>
        <Grid item xs={12} sm={4} md={3}sx={{ minHeight: 125 , minWidth:'32%'}}><KpiCard title="Negative Mentions" value={neg.toLocaleString()} insight={calculateChange(neg, prevNeg)} isPositive={neg <= prevNeg} /></Grid>
         <Grid item xs={12} sm={4} md={3}sx={{ minHeight: 125 , minWidth:'32.5%'}}><KpiCard title="Neutral Mentions" value={neu.toLocaleString()} insight={calculateChange(neu, prevNeu)} isPositive={true} /></Grid>
      </Grid>

      
<Box sx={{ display: 'flex', gap: 2, mb: 3, overflowX: 'auto', width: '100%' }}>
        <Box sx={{ minWidth: 360, flex: '1 0 50%' }}>
          <Card sx={{ height: 820, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Recent Mentions</Typography>
              <Box sx={{ overflow: 'auto', flex: 1 }}>
                {(filteredMentions || []).slice(0, 25).map(m => <MentionCard key={m.id || m.postUrl} mention={m} />)}
              </Box>
            </CardContent>
          </Card>
        </Box>
	

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4} sx={{ minHeight: 250, minWidth:'100%' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" >Sentiment Overview</Typography>
              <Box sx={{ flex: 1, minHeight: 120 }}>
                <Doughnut data={doughnutData} options={{ ...chartOptions, plugins: { legend: { position: 'bottom' } } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sx={{ minHeight: 250,minWidth:'100%' }}>
      
            <Card sx={{ height: '100%' }}><CardContent sx={{ height: 250 }}>
              <Typography variant="h6">Mentions Over time</Typography>
              <Box sx={{height: 'calc(100% - 30px)', position: 'relative'}}><Line data={mentionsOverTimeData} options={{ ...chartOptions, plugins: { legend: { display: true, position: 'bottom' } } }} />
              </Box>
              </CardContent>
            </Card>
            
        </Grid>

        <Grid item xs={12} md={4} sx={{ minHeight: 250,minWidth:'100%'  }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6">Mentions by Platform</Typography>
              <Box sx={{ flex: 1, minHeight: 120 }}><Bar data={mentionsByPlatformData} options={chartOptions} /></Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      	</Box>
      {/* Mentions Feed & Topic Cloud: always side-by-side but scroll horizontally if not enough width */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, overflowX: 'auto', width: '100%' }}>
      
                  <Box sx={{ minWidth: 460, flex: "1 0 50%" }}>
  <Card sx={{ height: 650,minWidth: 560, display: "flex", flexDirection: "column" }}>
    <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Typography variant="h6" gutterBottom>Topic Cloud</Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <WordCloud
          data={topicCloudData.map(t => ({ text: t.word, value: t.count }))}
          font="Inter"
          fontSize={word => Math.log2(word.value) * 5 + 12}  // scale sizes
          rotate={() => (Math.random() > 0.5 ? 0 : 90)}      // horiz + vertical only
          padding={2}
          fill={() => ["#111", "#333", "#555", "#777"][Math.floor(Math.random() * 9)]}
          width={500}
          height={500}
        />
      </Box>
</CardContent>
          </Card>
        </Box>
          <Box sx={{ minWidth: 260, flex: '1 0 50%' }}>
          <Card sx={{ height: 650,display: "flex", flexDirection: "column"}}>
                     <CardContent sx={{ height: '100%',flex: 1, display: "flex", flexDirection: "column" }}>
                        <Typography variant="h6" gutterBottom >Map View</Typography>
                          <Box sx={{ flex: 1, minHeight: 0 }}>
                  
                           <MapContainer center={[30.3753, 69.3451]} zoom={5} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                {locations.map(loc => (
                                    <Marker key={loc.name} position={[loc.lat, loc.lon]}>
                                        <Popup>
                                            {loc.name}
                                        </Popup>
                                    </Marker>
                                ))}
                            </MapContainer>
                        </Box>
                    </CardContent>
                </Card>
        </Box>
      </Box>

    </Container>
  );
}


export default DashboardPage;

